import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import {
  Box,
  Camera,
  ChevronDown,
  Download,
  FolderOpen,
  Lock,
  LogOut,
  MoreHorizontal,
  Save,
  XCircle,
} from 'lucide-react'
import { useAppStore } from '@core/store'
import { useIsMobile } from '@core/useIsMobile'
import { useAuth } from '@core/auth/useAuth'
import { schemaLabel } from '@viewer/lib/ifcSchema'
import { placementsDiffer } from '@viewer/lib/geoTransform'
import { rawIfc } from '@viewer/lib/rawIfc'
import { captureLocalViewpoint } from '@viewer/lib/viewpointCapture'
import { setPendingViewpointId } from '@modules/bcf/pendingViewpoint'
import { moduleRegistry } from '@modules/registry'
import { modulosNaInterface, totalPartilhados } from '@core/partilhas/partilhasRegras'
import {
  SessionParseError,
  blobToDataUrl,
  buildSession,
  dataUrlToBlob,
  pickSessionSaveHandle,
  readSessionContainer,
  sessionBelongsTo,
  sessionFileName,
  writeSessionFile,
  type VnSerialViewpoint,
  type VnSessionIfcBytes,
} from '@core/session/session'
import {
  applyLocalWorkSnapshot,
  collectLocalWorkSnapshot,
  markWorkSaved,
} from '@core/session/recovery'
import { useUnsavedWork } from '@core/session/useUnsavedWorkGuard'
import { lerSeccaoConflitos, seccaoDoLocalWork } from '@viewer/clash/clashSessao'
import {
  reporSeccaoConflitos,
  seccaoConflitosActual,
  semTriagemDosConflitos,
} from '@viewer/clash/clashSessaoEstado'
import { bcfPersistence } from '@modules/bcf/lib/bcfPersistence'
import { defaultExtensions } from '@modules/bcf/lib/bcfParser'
import { viewpointsStore } from '@viewer/lib/viewpointsStore'
import { ifcPersistence } from '@core/session/ifcPersistence'
import { fragmentPersistence } from '@core/session/fragmentPersistence'
import { viewerApi } from '@viewer/lib/viewerApi'
import { closeSession } from '@core/session/closeSession'
import { useModelosPartilhados } from '@core/modelos/modelosPartilhados'
import { encontrarPorSha, shasPorReferencia } from '@core/modelos/modelosRegras'
import HistoryButton from '@core/history/HistoryButton'
import Diagnostics from './Diagnostics'
import GlobalSearch from '@core/layout/search/GlobalSearch'
import TemaToggle from './TemaToggle'
import SinoNotificacoes from '@core/notificacoes/SinoNotificacoes'
import MenuPartilharSessao from '@core/sessoes/MenuPartilharSessao'
import IndicadorSessaoPartilhada from '@core/sessoes/IndicadorSessaoPartilhada'
import { esquecerSessaoActual } from '@core/sessoes/sessaoPartilhadaEstado'
import { useEscape, useShortcutHandler } from '@core/shortcuts'

function Topbar() {
  const navigate = useNavigate()
  const location = useLocation()
  // Em telemóvel o grupo de ações é `shrink-0` e media 434px num ecrã de
  // 375px: transbordava para lá da margem direita e era isso que fazia a
  // página esticar na horizontal. As peças largas (chip do modelo,
  // Capturar → BCF, Exportar IFC, Info do modelo) saem da faixa nesse
  // tamanho; ficam os botões-ícone, que cabem. No desktop nada muda.
  const isMobile = useIsMobile()
  const sessionInputRef = useRef<HTMLInputElement | null>(null)
  const activeModel = useAppStore((s) => {
    if (!s.activeModelId) return null
    return s.models.find((m) => m.id === s.activeModelId) ?? null
  })
  const models = useAppStore((s) => s.models)
  const bcfLoaded = useAppStore((s) => s.bcfLoaded)
  const unsavedWork = useUnsavedWork()
  const layoutMode = useAppStore((s) => s.workspaceLayoutMode)
  const setLayoutMode = useAppStore((s) => s.setWorkspaceLayoutMode)
  const setSessionRestoreModels = useAppStore((s) => s.setSessionRestoreModels)
  const { mode, user, workspace, modules, partilhados, logout } = useAuth()
  const isCloud = mode === 'cloud'
  // O menu Sessão também vale em modo local. Antes só existia em cloud,
  // mas o aviso de "trabalho por gravar" dispara nos dois: um aviso sem
  // maneira de gravar é pior do que não avisar. Em local o ficheiro é a
  // ÚNICA forma de o trabalho sobreviver, por isso é aqui que faz mais
  // falta.
  const isLocal = mode === 'local'
  const showSessionMenu = (isCloud && !!workspace) || isLocal
  // Modelos partilhados no workspace: com algum aberto, o menu oferece
  // a sessão leve (modelos por referência em vez de bytes no ficheiro).
  const modelosPart = useModelosPartilhados()
  const haPartilhadosAbertos =
    !!modelosPart?.estado?.ativo &&
    models.some((m) => !!encontrarPorSha(modelosPart.lista, m.sha256))
  const enabled = new Set(modules)
  const naInterface = new Set(modulosNaInterface(modules, partilhados))
  const tabs = isCloud && workspace
    ? moduleRegistry.filter((m) => naInterface.has(m.slug))
    : []
  // "Partilhados comigo": para quem tem alguma coisa de outro workspace,
  // e sempre para um convidado (é a página dele).
  const mostrarPartilhados =
    isCloud && !!workspace && (totalPartilhados(partilhados) > 0 || !!workspace.convidado)
  // The capture-and-create-topic flow only makes sense when the user is
  // looking at the viewer with a model loaded AND the workspace has BCF
  // enabled. The button hides outside that intersection.
  const onViewerRoute =
    isCloud && !!workspace &&
    (location.pathname === `/${workspace.slug}` ||
      location.pathname === `/${workspace.slug}/`)
  const showCapture =
    isCloud && !!workspace && enabled.has('bcf') && !!activeModel
    && onViewerRoute && !isMobile

  // "Exportar IFC" via the IFCEdit module: client-side download of
  // the bytes the viewer has in cache. The server-side round-trip
  // through ifcopenshell was retired alongside the BoQ pivot; the
  // module is now overlay-only, with web-ifc-side persistence
  // landing in the next sub-sprint. For now this just downloads
  // the original bytes; once persistence ships, the button will
  // serialize the modified web-ifc model.
  const showExportIfc =
    isCloud && !!workspace && enabled.has('ifc-edit') && !!activeModel
    && !isMobile
  const [exporting, setExporting] = useState<'idle' | 'busy'>('idle')
  const [exportError, setExportError] = useState<string | null>(null)

  const onExportIfc = () => {
    if (!activeModel) return
    const bytes = rawIfc.getCachedBytes(activeModel.id)
    if (!bytes) {
      setExportError(
        'Bytes do IFC não estão em cache. Recarrega o modelo e tenta de novo.',
      )
      return
    }
    setExporting('busy')
    setExportError(null)
    try {
      const blob = new Blob([bytes.slice().buffer], {
        type: 'application/octet-stream',
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = activeModel.fileName
      document.body.appendChild(a)
      a.click()
      a.remove()
      // Revoke after a tick so the browser has a chance to start
      // the download before the URL goes away.
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (err) {
      setExportError(
        err instanceof Error ? err.message : 'Falha ao exportar IFC.',
      )
    } finally {
      setExporting('idle')
    }
  }

  const onCaptureView = async () => {
    if (!workspace) return
    const bcfActiveId = useAppStore.getState().bcfActiveId
    try {
      const record = await captureLocalViewpoint({
        name: `Captura ${new Date().toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}`,
      })
      setPendingViewpointId(record.id)
    } catch (err) {
      setExportError(
        err instanceof Error ? err.message : 'Falha a capturar viewpoint.',
      )
      return
    }
    navigate(`/${workspace.slug}/bcf/${bcfActiveId ?? ''}`)
  }

  const handleSaveSession = async (opts: { leve?: boolean } = {}) => {
    if (!showSessionMenu) return
    // STEP 1: Acquire the save handle synchronously inside the click
    //   gesture. Chromium's showSaveFilePicker requires "transient
    //   activation" that's consumed by the first await; if any data
    //   prep happens before this call the picker silently rejects and
    //   we degrade to anchor download. The handle stays writable
    //   after activation expires, so the heavy work below runs after.
    const suggestedName = sessionFileName(workspace?.slug ?? null)
    const pick = await pickSessionSaveHandle(suggestedName)
    if (pick.kind === 'cancelled') return // user dismissed; do nothing
    const handle = pick.kind === 'handle' ? pick.handle : null

    // STEP 2: Heavy data prep. Awaits are safe from here on.
    // Em local não há rota a guardar: o router serve o viewer em
    // qualquer caminho e o URL não escolhe nada.
    const prefix = workspace ? `/${workspace.slug}/` : null
    const route =
      prefix && location.pathname.startsWith(prefix)
        ? location.pathname.slice(prefix.length)
        : ''
    const state = useAppStore.getState()
    const bcfs = state.bcfLoaded
    const bcfActiveId = state.bcfActiveId
    let viewpoints: VnSerialViewpoint[] = []
    try {
      const list = await viewpointsStore.list()
      viewpoints = await Promise.all(
        list.map(async (vp) => {
          const { thumbnail, ...rest } = vp
          let dataUrl: string | null = null
          let mime: string | null = null
          if (thumbnail) {
            try {
              dataUrl = await blobToDataUrl(thumbnail)
              mime = thumbnail.type || null
            } catch (err) {
              console.warn('[session] thumbnail encode failed', err)
            }
          }
          return {
            record: rest,
            thumbnail_data_url: dataUrl,
            thumbnail_mime: mime,
          }
        }),
      )
    } catch (err) {
      console.warn('[session] failed to enumerate viewpoints', err)
    }
    // Geo placement overrides (Onda C): for each loaded model, if the
    // user manually overrode the IfcMapConversion via the sidebar's
    // "Posição" editor, embed the override into the session so reload
    // restores it. We only persist overrides that DIFFER from the
    // baseline: if the override equals the auto-derived placement
    // there's no point writing it (the loader would just compute the
    // same value again).
    const geoBaselines = state.geoBaselines
    const geoOverrides = state.geoOverrides
    // Sessão leve: os modelos partilhados no workspace vão pelo SHA-256 e
    // os bytes não entram no ficheiro (quem abre descarrega-os de lá).
    const listaPartilhados = modelosPart?.lista ?? null
    const porReferencia = shasPorReferencia(models, listaPartilhados, !!opts.leve)
    const session = buildSession({
      workspaceSlug: workspace?.slug ?? '',
      route,
      search: workspace ? location.search.replace(/^\?/, '') : '',
      layoutMode,
      models: models.map((m) => {
        const override = geoOverrides[m.id]
        const baseline = geoBaselines[m.id]
        const persist =
          override && (!baseline || placementsDiffer(override, baseline))
            ? override
            : null
        return {
          filename: m.fileName,
          sha256: m.sha256,
          schema_version: m.schemaVersion,
          placement_override: persist,
          source_format: m.sourceFormat ?? 'ifc',
          ...(encontrarPorSha(listaPartilhados, m.sha256) ? { partilhado: true } : {}),
        }
      }),
      bcfs,
      bcfActiveId,
      viewpoints,
      // v5: as vistas 2D (cortes, anotações, cotas, cores) e os
      // rascunhos IDS/IDSX vivem em localStorage. Sem isto, gravar a
      // sessão deixava-os de fora e recarregar perdia-os.
      // A triagem dos conflitos tem secção própria (v7): não vai no
      // local_work, para a reposição a poder confirmar.
      localWork: semTriagemDosConflitos(collectLocalWorkSnapshot()),
      conflitos: seccaoConflitosActual(),
    })
    // Snapshot the bytes of every loaded IFC into the session zip so
    // the file is portable across browsers and machines. Bytes are
    // looked up first in the in-memory rawIfc cache (latest, most
    // likely populated), then in the IDB cache (`ifcPersistence`)
    // for models the user loaded earlier and that rawIfc may have
    // evicted. Misses are skipped with a warning so a partial save
    // is still useful.
    const ifcBytes: VnSessionIfcBytes[] = []
    for (const m of models) {
      if (porReferencia.has(m.sha256.toLowerCase())) continue
      if (m.sourceFormat === 'frag') {
        try {
          const buffer = await viewerApi.getFragmentsBuffer(m.id)
          ifcBytes.push({
            sha256: m.sha256,
            filename: m.fileName,
            bytes: new Uint8Array(buffer),
            sourceFormat: 'frag',
          })
        } catch (err) {
          console.warn('[session] fragment serialization failed', m.id, err)
        }
        continue
      }
      const cached = rawIfc.getCachedBytes(m.id)
      if (cached) {
        ifcBytes.push({
          sha256: m.sha256,
          filename: m.fileName,
          bytes: cached,
        })
        continue
      }
      try {
        const rec = await ifcPersistence.load(m.sha256)
        if (rec) {
          const buf = await rec.blob.arrayBuffer()
          ifcBytes.push({
            sha256: m.sha256,
            filename: m.fileName,
            bytes: new Uint8Array(buf),
          })
          continue
        }
      } catch (err) {
        console.warn('[session] ifcPersistence.load failed', m.sha256, err)
      }
      console.warn(
        `[session] no bytes for IFC ${m.fileName} (sha ${m.sha256.slice(0, 12)}…): saved without it`,
      )
    }
    // STEP 3: Write to the handle (or anchor fallback when null).
    try {
      const res = await writeSessionFile(handle, suggestedName, session, ifcBytes)
      // Gravou: o trabalho deixa de estar por gravar. Sem marca na
      // barra, sem aviso ao sair e sem diálogo de recuperação no
      // arranque seguinte, até se mexer outra vez.
      if (res.saved) markWorkSaved()
    } catch (err) {
      console.warn('[session] save failed', err)
      window.alert('Não foi possível guardar a sessão.')
    }
  }

  // Keyboard shortcuts. Save/Open Session use the same handlers the
  // toolbar buttons use. The keypress is itself a user gesture, so
  // showSaveFilePicker still gets transient activation. Open delegates
  // to the hidden <input> via .click() so the picker dialog opens.
  useShortcutHandler(
    'session.save',
    () => {
      void handleSaveSession()
    },
    [handleSaveSession],
  )
  useShortcutHandler(
    'session.open',
    () => {
      sessionInputRef.current?.click()
    },
    [],
  )

  const handleOpenSessionFile = async (file: File | null) => {
    if (!file) return
    if (!showSessionMenu) return
    try {
      const { session, ifcBytes } = await readSessionContainer(file)
      if (!sessionBelongsTo(session, workspace?.slug ?? null)) {
        window.alert(
          `Esta sessão é do workspace "${session.workspace_slug}". `
            + `Sai e entra nesse workspace para a abrir.`,
        )
        return
      }

      // Abrir uma sessão é abrir um documento: substitui a que está
      // aberta. Os BCF e os viewpoints já eram substituídos, mas os IFC
      // abertos ficavam e juntavam-se aos da sessão, e o markWorkSaved()
      // lá em baixo dava-os por gravados sem estarem no ficheiro.
      const abertos = useAppStore.getState().models.length
      if (abertos > 0) {
        const ok = window.confirm(
          `Abrir esta sessão fecha a sessão atual (${abertos} modelo(s) aberto(s)). `
            + 'O que não foi gravado com "Guardar sessão" perde-se. Continuar?',
        )
        if (!ok) return
        await closeSession()
      }
      // Um ficheiro de sessão não é a sessão partilhada em que se estava.
      esquecerSessaoActual()

      // Navigate to the viewer base FIRST so any module panel
      // (BCF, BoQ, IFCEdit, …) unmounts before the new state lands.
      // Re-mounting against the new BCF/viewpoint store is safer than
      // letting the active panel rerender mid-restore; that's what
      // tripped the "length of undefined" crash on the previous
      // deploy. The final navigate at the bottom moves to the saved
      // route once the new state is in place. Em local não há rotas:
      // o ViewerShell é a página toda e navegar não faria nada.
      if (workspace) navigate(`/${workspace.slug}/`, { replace: false })

      // Prime the per-browser IFC byte cache. SessionRestoreBanner
      // picks these up silently, no manual file-picker step.
      if (ifcBytes.size > 0) {
        for (const m of session.models) {
          const bytes = ifcBytes.get(m.sha256.toLowerCase())
          if (!bytes) continue
          try {
            if (m.source_format === 'frag') {
              await fragmentPersistence.save({
                sourceSha256: m.sha256,
                filename: m.filename,
                sourceFormat: 'frag',
                buffer: bytes,
              })
            } else {
              await ifcPersistence.save({
                sha256: m.sha256,
                filename: m.filename,
                bytes,
              })
            }
          } catch (err) {
            console.warn('[session] prime ifcPersistence failed', m.sha256, err)
          }
        }
      }

      // Restore embedded viewpoints BEFORE BCFs so any topic
      // referencing a viewpoint can resolve it on first render.
      if (Array.isArray(session.viewpoints)) {
        try {
          await viewpointsStore.clearAll()
          for (const vp of session.viewpoints) {
            let blob: Blob | null = null
            if (vp.thumbnail_data_url) {
              const decoded = dataUrlToBlob(vp.thumbnail_data_url)
              if (decoded && vp.thumbnail_mime) {
                blob = new Blob([await decoded.arrayBuffer()], {
                  type: vp.thumbnail_mime,
                })
              } else {
                blob = decoded
              }
            }
            await viewpointsStore.save({ ...vp.record, thumbnail: blob })
          }
          const refresh = useAppStore.getState().refreshViewpoints
          if (typeof refresh === 'function') void refresh()
        } catch (err) {
          console.warn('[session] viewpoint restore failed', err)
        }
      }

      // Restore embedded BCFs (v3+). Defensive normalisation: any
      // topic whose `viewpointRefs` / `watchers` field is missing
      // (legacy shape pre-2026-05-08) gets coerced to `[]`. Same for
      // `extensions` on the parsed BCF; older snapshots don't have
      // it; we seed defaults so the UI doesn't crash on dropdown
      // populations.
      //
      // Em modo local os BCFs ficam no ficheiro mas NÃO se repõem: o
      // módulo BCF é do workspace (em local nem separador tem), e
      // encher a memória com projetos que não há como abrir só serviria
      // para o chip dizer "sessão ativa" sem nada para ver. O ficheiro
      // não é tocado: aberto depois em cloud, os BCFs voltam.
      if (Array.isArray(session.bcfs) && !workspace && session.bcfs.length > 0) {
        console.info(
          `[session] ${session.bcfs.length} BCF(s) ignorados: o módulo BCF `
            + 'não existe em modo local (ficam no ficheiro).',
        )
      }
      if (Array.isArray(session.bcfs) && workspace) {
        const normalised = session.bcfs.map((bcf) => ({
          ...bcf,
          versions: (bcf.versions ?? []).map((v) => ({
            ...v,
            parsed: {
              ...v.parsed,
              topics: (v.parsed?.topics ?? []).map((t) => ({
                ...t,
                viewpointRefs: t.viewpointRefs ?? [],
                comments: t.comments ?? [],
                labels: t.labels ?? [],
                viewpointCount: t.viewpointCount ?? 0,
                watchers: t.watchers ?? [],
                stage: t.stage ?? null,
              })),
              warnings: v.parsed?.warnings ?? [],
              extensions: v.parsed?.extensions ?? defaultExtensions(),
            },
          })),
        }))
        try {
          await bcfPersistence.saveAll(normalised)
        } catch (err) {
          console.warn('[session] bcf persistence write failed', err)
        }
        try {
          useAppStore.setState({
            bcfLoaded: normalised,
            bcfActiveId:
              session.bcf_active_id ?? normalised[0]?.localId ?? null,
            bcfHydrated: true,
          })
        } catch (err) {
          console.warn('[session] bcf state set failed', err)
        }
      }

      // v5: repor o trabalho que vive em localStorage (vistas 2D,
      // rascunhos). Antes da navegação, para os painéis já montarem
      // com o conteúdo certo.
      if (Array.isArray(session.local_work)) {
        try {
          applyLocalWorkSnapshot(semTriagemDosConflitos(session.local_work))
        } catch (err) {
          console.warn('[session] local work restore failed', err)
        }
      }
      // v7: a detecção de conflitos (conjuntos, configuração, triagem,
      // histórico). As sessões antigas traziam-na no local_work. A
      // triagem do browser só é substituída se o utilizador confirmar,
      // quando forem diferentes.
      try {
        const conflitos =
          lerSeccaoConflitos(session.conflitos) ?? seccaoDoLocalWork(session.local_work)
        if (conflitos) reporSeccaoConflitos(conflitos, (m) => window.confirm(m))
      } catch (err) {
        console.warn('[session] clash restore failed', err)
      }

      try {
        setLayoutMode(session.layout_mode)
      } catch (err) {
        console.warn('[session] setLayoutMode failed', err)
      }
      try {
        setSessionRestoreModels(session.models)
      } catch (err) {
        console.warn('[session] setSessionRestoreModels failed', err)
      }
      // Abrir um ficheiro é como abrir um documento: o que está no ecrã
      // corresponde ao que está no disco, logo não há nada por gravar.
      markWorkSaved()
      // Em modo local não se navega de todo. Um ficheiro gravado em
      // local traz a rota vazia, e cai no viewer, que é o que existe.
      if (workspace) {
        const target =
          `/${workspace.slug}/${session.route}`
          + (session.search ? `?${session.search}` : '')
        navigate(target, { replace: false })
      }
    } catch (err) {
      if (err instanceof SessionParseError) {
        window.alert(err.message)
      } else {
        console.error('[session] open failed', err)
        window.alert('Não foi possível abrir o ficheiro de sessão.')
      }
    } finally {
      if (sessionInputRef.current) sessionInputRef.current.value = ''
    }
  }
  // "Fechar sessão" replicated from the retired SessionPill: same
  // confirmation copy, same closeSession() teardown, same navigate to
  // the workspace root afterwards.
  const hasActiveSession = models.length > 0 || bcfLoaded.length > 0
  const handleCloseSession = async () => {
    if (!showSessionMenu) return
    const parts: string[] = []
    if (models.length > 0) parts.push(`${models.length} IFC carregado(s)`)
    if (bcfLoaded.length > 0) parts.push(`${bcfLoaded.length} BCF carregado(s)`)
    const summary = parts.length > 0 ? ` (${parts.join(', ')})` : ''
    const confirmText = `Fechar a sessão${summary}? Tudo o que está em memória nesta sessão vai ser limpo: IFCs, BCFs, viewpoints. Os ficheiros .bcfzip que tens guardados no disco continuam intactos, re-importa-os para retomar.`
    if (!window.confirm(confirmText)) return
    await closeSession()
    if (workspace) navigate(`/${workspace.slug}/`, { replace: true })
  }

  const tabClass = ({ isActive }: { isActive: boolean }) =>
    `rounded px-2 py-0.5 text-xs ${
      isActive
        ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
        : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
    }`

  // Separador do módulo já activo: em vez de navegar para o sítio onde
  // já estamos (que não fazia nada), fecha o painel e devolve a página
  // ao viewer. Só no desktop: em telemóvel o módulo é a página inteira e
  // não há painel para fechar, por isso o separador continua a navegar.
  const onModuleTabClick = (slug: string) => (e: React.MouseEvent) => {
    if (isMobile || !workspace) return
    const base = `/${workspace.slug}/${slug}`
    const isOpen =
      location.pathname === base || location.pathname.startsWith(`${base}/`)
    if (!isOpen) return
    e.preventDefault()
    navigate(`/${workspace.slug}/`)
  }

  // Sem overflow no <header>: um overflow-x-auto aqui tornava o
  // overflow-y = auto e prendia os dropdowns (Sessão, ⋯, utilizador,
  // Histórico, Diagnóstico) dentro da faixa de 40px. O scroll horizontal,
  // para quando a barra fica apertada, vive só no grupo do meio
  // (workspace + tabs), que não tem menus a expandir.
  return (
    <header className="flex h-10 shrink-0 items-center gap-3 border-b border-[var(--color-border)] bg-[var(--color-panel)] px-3 text-sm">
      <Link
        to={isCloud && workspace ? `/${workspace.slug}/` : '/'}
        className="flex shrink-0 items-center gap-2 text-[var(--color-text)] hover:opacity-80"
        title="VN2R openBIM Hub, voltar ao viewer"
      >
        <span className="text-sm font-semibold tracking-wider text-[var(--color-text)]">
          VN2R
        </span>
        <span className="text-xs font-medium tracking-tight text-[var(--color-text-muted)]">
          <span className="text-[var(--color-accent)]">openBIM</span> Hub
        </span>
      </Link>
      {/* Grupo do meio: encolhe (min-w-0) e faz scroll horizontal próprio
          quando aperta, absorvendo a folga em vez de empurrar as ações
          para fora do ecrã. flex-1 ocupa o espaço e mantém as ações
          encostadas à direita. */}
      <div className="flex min-w-0 flex-1 items-center gap-3 overflow-x-auto">
        {isCloud && workspace ? (
          <>
            <span className="text-[var(--color-text-dim)]">·</span>
            <span className="truncate text-xs text-[var(--color-text-muted)]">
              {workspace.name}
            </span>
          </>
        ) : (
          <>
            <span className="text-[var(--color-text-dim)]">·</span>
            <span className="text-xs text-[var(--color-text-muted)]">
              Modo local
            </span>
          </>
        )}
        {isCloud && workspace && (
          <nav className="flex shrink-0 items-center gap-1">
            <NavLink to={`/${workspace.slug}/`} end className={tabClass}>
              Viewer
            </NavLink>
            {tabs.map((m) => (
              <NavLink
                key={m.slug}
                to={`/${workspace.slug}/${m.slug}/`}
                className={tabClass}
                onClick={onModuleTabClick(m.slug)}
                title={
                  isMobile
                    ? m.label
                    : `${m.label}: abrir no painel junto ao 3D (clicar outra vez fecha)`
                }
              >
                {m.label}
              </NavLink>
            ))}
            {mostrarPartilhados && (
              <NavLink
                to={`/${workspace.slug}/partilhados`}
                className={tabClass}
                title="O que outras pessoas partilharam consigo a partir de outros workspaces"
              >
                Partilhados comigo
              </NavLink>
            )}
          </nav>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1.5">
        {/* Input escondido para "Abrir sessão"; fica montado sempre que
            o menu de sessão pode aparecer. */}
        {showSessionMenu && (
          <input
            ref={sessionInputRef}
            type="file"
            accept=".vn2r-session,.json,application/json"
            className="hidden"
            onChange={(e) =>
              void handleOpenSessionFile(e.target.files?.[0] ?? null)
            }
          />
        )}

        {/* Chip do modelo activo: ícone + nome + schema + ponto de
            sessão ativa. */}
        {activeModel && !isMobile && (
          <div
            className="flex shrink-0 items-center gap-1.5 rounded-md border-[0.5px] border-[var(--color-border)] bg-[var(--color-surface)]/50 px-2 py-0.5"
            title={activeModel.fileName}
          >
            <Box
              size={13}
              className="shrink-0 text-[var(--color-text-muted)]"
            />
            <span className="max-w-[150px] truncate text-xs text-[var(--color-text-muted)]">
              {activeModel.fileName}
            </span>
            {/* O IFC 4.3 já não leva aviso: as peças varridas por curva
                passaram a ter geometria com o web-ifc da beta.201. */}
            <span className="rounded border border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] px-1.5 py-0 text-[10px] font-medium text-[var(--color-accent)]">
              {schemaLabel(activeModel.schemaVersion)}
            </span>
            {isCloud && (models.length > 0 || bcfLoaded.length > 0) && (
              <span
                className="inline-block h-2 w-2 shrink-0 rounded-full bg-emerald-400"
                title="Sessão ativa neste separador"
              />
            )}
          </div>
        )}

        {/* Grupo de ações (verbos). */}
        {(showCapture || showExportIfc || showSessionMenu) && (
          <TopbarSeparator />
        )}
        {showCapture && (
          <button
            type="button"
            onClick={() => void onCaptureView()}
            className="flex shrink-0 items-center gap-1 rounded border border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] px-2 py-0.5 text-xs font-medium text-[var(--color-accent)] hover:text-[var(--color-accent-hover)]"
            title="Capturar vista atual e criar tópico BCF"
          >
            <Camera size={13} />
            Capturar → BCF
          </button>
        )}
        {showExportIfc && (
          <button
            type="button"
            onClick={() => void onExportIfc()}
            disabled={exporting === 'busy'}
            className="flex shrink-0 items-center gap-1 rounded border border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] px-2 py-0.5 text-xs font-medium text-[var(--color-accent)] hover:text-[var(--color-accent-hover)] disabled:opacity-60"
            title={
              exportError
                ? exportError
                : 'Exportar o IFC actual (round-trip via servidor IFCEdit)'
            }
          >
            <Download size={13} />
            {exporting === 'busy' ? 'A exportar…' : 'Exportar IFC'}
          </button>
        )}
        <IndicadorSessaoPartilhada compacto={isMobile} />
        {showSessionMenu && (
          <TopbarMenu
            align="right"
            title={
              unsavedWork
                ? 'Há trabalho por gravar nesta sessão. Guardar sessão grava tudo num ficheiro .vn2r-session.'
                : 'Sessão de trabalho: abrir, guardar ou fechar.'
            }
            triggerClassName={
              'flex shrink-0 items-center gap-1 rounded border px-2 py-0.5 text-xs hover:text-[var(--color-text)] ' +
              (unsavedWork
                ? 'border-[var(--color-accent-border)] text-[var(--color-accent)]'
                : 'border-[var(--color-border)] text-[var(--color-text-muted)]')
            }
            triggerContent={(open) => (
              <>
                <FolderOpen size={13} />
                {/* Marca discreta de "por gravar": ponto âmbar no chip
                    da sessão, com as palavras só onde há espaço. */}
                {unsavedWork && (
                  <span
                    data-testid="unsaved-dot"
                    className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--color-accent)]"
                    aria-hidden
                  />
                )}
                {!isMobile && (
                  <>
                    {unsavedWork ? 'Sessão · não gravada' : 'Sessão'}
                    <ChevronDown
                      size={12}
                      className={
                        'transition-transform ' + (open ? 'rotate-180' : '')
                      }
                    />
                  </>
                )}
              </>
            )}
            panelClassName="w-56 p-1"
          >
            {(close) => (
              <>
                <button
                  type="button"
                  className={menuItemClass}
                  title="Abrir uma sessão de trabalho previamente guardada (.vn2r-session)."
                  onClick={() => {
                    close()
                    sessionInputRef.current?.click()
                  }}
                >
                  <FolderOpen size={14} />
                  Abrir sessão
                </button>
                <button
                  type="button"
                  className={menuItemClass}
                  title="Guardar a sessão num ficheiro .vn2r-session: rota, layout, BCFs, viewpoints e snapshot dos IFCs (bytes incluídos no zip). Abre o picker do sistema para escolheres nome e local; em browsers sem picker, vai para a pasta de Downloads."
                  onClick={() => {
                    close()
                    void handleSaveSession()
                  }}
                >
                  <Save size={14} />
                  Guardar sessão
                </button>
                {haPartilhadosAbertos && (
                  <button
                    type="button"
                    className={menuItemClass}
                    title="Guardar a sessão sem os bytes dos modelos partilhados no workspace: ficam referenciados pelo SHA-256 e quem abrir a sessão descarrega-os do workspace. Os IFC não partilhados continuam dentro do ficheiro."
                    onClick={() => {
                      close()
                      void handleSaveSession({ leve: true })
                    }}
                  >
                    <Save size={14} />
                    Guardar sessão leve
                  </button>
                )}
                <MenuPartilharSessao close={close} itemClassName={menuItemClass} />
                {hasActiveSession && (
                  <button
                    type="button"
                    className={
                      menuItemClass +
                      ' text-[var(--color-error-text)] hover:!text-[var(--color-error-text)]'
                    }
                    title="Fechar sessão (descarrega IFCs e limpa o projeto BCF ativo)."
                    onClick={() => {
                      close()
                      void handleCloseSession()
                    }}
                  >
                    <XCircle size={14} />
                    Fechar sessão
                  </button>
                )}
              </>
            )}
          </TopbarMenu>
        )}
        {!isMobile && <ModelInfoToggle />}

        {/* Ferramentas com popover próprio (histórico e diagnóstico):
            ficam FORA de qualquer menu para o posicionamento absoluto e
            o click-outside deles não partirem dentro doutro popover. */}
        <TopbarSeparator />
        {/* A pesquisa fica ao pe do resto das ferramentas e nao dentro
            de um menu: e' um campo, e um campo dentro de um menu que
            fecha ao clicar fora nao se consegue usar. */}
        {!isMobile && <GlobalSearch />}
        <HistoryButton />
        {/* Notificações: só com sessão num workspace (em modo local não
            há servidor a quem perguntar). No telemóvel o sino não cabe
            na faixa: vai para o menu "Mais opções". */}
        {isCloud && workspace && !isMobile && (
          <SinoNotificacoes workspaceSlug={workspace.slug} />
        )}
        <TemaToggle />
        <Diagnostics />

        {/* Menu overflow: só itens simples e seguros. */}
        <TopbarMenu
          align="right"
          title="Mais opções"
          ariaLabel="Mais opções"
          triggerClassName="flex shrink-0 items-center rounded border border-[var(--color-border)] px-1.5 py-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
          triggerContent={() => <MoreHorizontal size={15} />}
          panelClassName="w-52 p-1"
        >
          {(close) => (
            <>
              {/* Em telemóvel o botão "Info do modelo" sai da faixa (não
                  cabe); a função não se perde, entra aqui. */}
              {isMobile && (
                <>
                  <ModelInfoMenuItem close={close} />
                  {isCloud && workspace && (
                    <Link
                      to={`/${workspace.slug}/notificacoes`}
                      className={menuItemClass}
                      onClick={close}
                    >
                      Notificações
                    </Link>
                  )}
                  <div className="my-1 border-t border-[var(--color-border)]" />
                </>
              )}
              {isCloud && user?.is_vn2r_admin && (
                <>
                  <Link
                    to={
                      workspace
                        ? `/${workspace.slug}/admin#audit`
                        : '/admin#audit'
                    }
                    className={menuItemClass}
                    title="Registo de atividade da plataforma: login, password, imports, alterações de membros, etc. (admin VN2R)"
                    onClick={close}
                  >
                    Atividade
                  </Link>
                  <Link
                    to={workspace ? `/${workspace.slug}/admin` : '/admin'}
                    className={menuItemClass}
                    onClick={close}
                  >
                    Admin
                  </Link>
                  <div className="my-1 border-t border-[var(--color-border)]" />
                </>
              )}
              <div className="flex items-center justify-between gap-2 px-2 py-1.5">
                <span className="text-xs text-[var(--color-text-muted)]">
                  Tamanho do texto
                </span>
                <UiScaleControl />
              </div>
            </>
          )}
        </TopbarMenu>

        {/* Menu do utilizador (cloud). */}
        {isCloud && user && (
          <TopbarMenu
            align="right"
            title={user.email}
            ariaLabel="Menu do utilizador"
            triggerClassName="flex shrink-0 items-center gap-1 rounded-full border border-[var(--color-border)] py-0.5 pl-0.5 pr-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
            triggerContent={(open) => (
              <>
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[var(--color-accent-bg)] text-xs font-semibold uppercase text-[var(--color-accent)]">
                  {user.email.slice(0, 1).toUpperCase()}
                </span>
                <ChevronDown
                  size={12}
                  className={
                    'transition-transform ' + (open ? 'rotate-180' : '')
                  }
                />
              </>
            )}
            panelClassName="w-60 p-1"
          >
            {(close) => (
              <>
                <div className="px-2 py-1.5">
                  <div
                    className="truncate text-xs font-medium text-[var(--color-text)]"
                    title={user.email}
                  >
                    {user.email}
                  </div>
                  {workspace && (
                    <div className="truncate text-[10px] text-[var(--color-text-muted)]">
                      {workspace.name}
                    </div>
                  )}
                </div>
                <div className="my-1 border-t border-[var(--color-border)]" />
                <Link
                  to={
                    workspace
                      ? `/${workspace.slug}/account/password`
                      : '/account/password'
                  }
                  className={menuItemClass}
                  title="Definir ou alterar password"
                  onClick={close}
                >
                  <Lock size={14} />
                  Alterar password
                </Link>
                <button
                  type="button"
                  className={menuItemClass}
                  onClick={() => {
                    close()
                    void logout()
                  }}
                >
                  <LogOut size={14} />
                  Terminar sessão
                </button>
                <div className="my-1 border-t border-[var(--color-border)]" />
                <a
                  href="https://www.vn2r.pt"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-2 py-1.5 text-[10px] text-[var(--color-text-dim)] hover:text-[var(--color-text-muted)]"
                  title="Abrir vn2r.pt"
                >
                  vn2r.pt
                </a>
              </>
            )}
          </TopbarMenu>
        )}

        {/* Modo local não tem menu de utilizador; o link vn2r.pt fica
            solto. */}
        {!(isCloud && user) && (
          <a
            href="https://www.vn2r.pt"
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 text-[10px] text-[var(--color-text-dim)] hover:text-[var(--color-text-muted)]"
            title="Abrir vn2r.pt"
          >
            vn2r.pt
          </a>
        )}
      </div>
    </header>
  )
}

// Estilo partilhado das linhas dos menus (Sessão, ⋯, utilizador).
const menuItemClass =
  'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs ' +
  'text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]'

// Separador vertical fino entre grupos da topbar.
function TopbarSeparator() {
  return (
    <span className="mx-0.5 h-5 w-px shrink-0 bg-[var(--color-border)]" />
  )
}

// Dropdown reutilizável com click-outside + Escape, seguindo o padrão
// de OriginKindsFilterButton (ribbon/filterButtons). O botão trigger e
// o conteúdo são fornecidos pelo chamador; `children` recebe um `close`
// para fechar o popover ao escolher uma opção. O useEffect só regista
// listeners (sem setState em effect), por isso é lint-clean.
function TopbarMenu({
  triggerContent,
  triggerClassName,
  title,
  ariaLabel,
  align = 'right',
  panelClassName = '',
  children,
}: {
  triggerContent: (open: boolean) => ReactNode
  triggerClassName?: string
  title?: string
  ariaLabel?: string
  align?: 'left' | 'right'
  panelClassName?: string
  children: (close: () => void) => ReactNode
}) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  // Escape fecha o menu antes de chegar à cascata do viewer.
  useEscape(open, () => setOpen(false))
  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: PointerEvent) => {
      const node = wrapRef.current
      if (node && !node.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('pointerdown', onPointerDown, { capture: true })
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('pointerdown', onPointerDown, {
        capture: true,
      })
      window.removeEventListener('keydown', onKey)
    }
  }, [open])
  return (
    <div ref={wrapRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={title}
        aria-label={ariaLabel}
        className={triggerClassName}
      >
        {triggerContent(open)}
      </button>
      {open && (
        <div
          className={
            'absolute top-full z-[900] mt-1 rounded-md border border-[var(--color-border)] bg-[var(--color-panel)] shadow-lg ' +
            (align === 'right' ? 'right-0 ' : 'left-0 ') +
            panelClassName
          }
          onPointerDown={(e) => e.stopPropagation()}
        >
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  )
}

// Toggle button for the floating "Info do modelo" window. The
// model-info panel used to be stacked above the properties in the
// right sidebar, which (a) competed for vertical space with the
// per-element details and (b) hid the categories/systems browser
// behind a long scroll. This pulls it into a dedicated draggable
// window that the user opens on demand.
function ModelInfoToggle() {
  const open = useAppStore((s) => s.modelInfoPopup.open)
  const setOpen = useAppStore((s) => s.setModelInfoOpen)
  useShortcutHandler('topbar.toggleModelInfo', () => setOpen(!open), [open])
  return (
    <button
      type="button"
      onClick={() => setOpen(!open)}
      className={
        'rounded border px-2 py-0.5 text-xs ' +
        (open
          ? 'border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
          : 'border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text)]')
      }
      title="Info do modelo: schema, unidades, modelos federados, categorias, sistemas/grupos/assemblies"
    >
      Info do modelo
    </button>
  )
}

// Mesma ação do ModelInfoToggle, em linha de menu. Usada só no modo
// telemóvel, onde o botão da faixa não cabe.
function ModelInfoMenuItem({ close }: { close: () => void }) {
  const open = useAppStore((s) => s.modelInfoPopup.open)
  const setOpen = useAppStore((s) => s.setModelInfoOpen)
  return (
    <button
      type="button"
      className={menuItemClass}
      onClick={() => {
        setOpen(!open)
        close()
      }}
      title="Info do modelo: schema, unidades, modelos federados, categorias, sistemas/grupos/assemblies"
    >
      {open ? 'Fechar info do modelo' : 'Info do modelo'}
    </button>
  )
}

// Three-button compact control for the global UI zoom (panels,
// popups, ribbon, sidebars, menus). Lives in the topbar so it's
// reachable from every workspace view. Persists via the store
// (localStorage), survives reloads.
function UiScaleControl() {
  const uiScale = useAppStore((s) => s.uiScale)
  const setUiScale = useAppStore((s) => s.setUiScale)
  const pct = Math.round(uiScale * 100)
  return (
    <div
      className="flex items-center gap-0.5 rounded border border-[var(--color-border)]"
      title="Tamanho do texto da interface (não afecta o canvas 3D)"
    >
      <button
        type="button"
        onClick={() => setUiScale(uiScale - 0.05)}
        disabled={uiScale <= 0.85}
        className="px-1.5 py-0.5 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-30"
        title="Diminuir"
      >
        A−
      </button>
      <button
        type="button"
        onClick={() => setUiScale(1)}
        className="min-w-[3ch] px-1 py-0.5 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
        title="Repor 100%"
      >
        {pct}%
      </button>
      <button
        type="button"
        onClick={() => setUiScale(uiScale + 0.05)}
        disabled={uiScale >= 1.6}
        className="px-1.5 py-0.5 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-30"
        title="Aumentar"
      >
        A+
      </button>
    </div>
  )
}

export default Topbar
